package com.appsyde.shared.presentation.screen.screen

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import com.appsyde.shared.app.presentation.account.AccountViewModel
import com.appsyde.shared.modules.account.domain.model.Me
import com.appsyde.shared.presentation.style.animations.AppStylesAnimations
import com.appsyde.shared.presentation.style.style.AppStylesAccount
import com.appsyde.shared.presentation.style.style.AppStylesGlobal
import com.appsyde.shared.presentation.style.style.AppStylesHeader
import com.appsyde.shared.presentation.style.style.AppStylesLayout
import org.jetbrains.compose.web.css.Style
import org.jetbrains.compose.web.dom.*
import org.koin.core.context.GlobalContext

/**
 * Vue de blocage produit : un seul bloc central compact, hiérarchie claire, sensation premium.
 * Topbar minimale (logo, Mon compte, Déconnexion). Pas d'accès Tickets / Répondre.
 */
@Composable
fun BillingGateScreen(
    meInfo: Me,
    onLogout: () -> Unit
) {
    val accountViewModel = remember { GlobalContext.get().get<AccountViewModel>() }
    val accountState by accountViewModel.state.collectAsState()
    var checkoutTrigger by remember { mutableStateOf(0) }
    LaunchedEffect(checkoutTrigger) {
        if (checkoutTrigger > 0) {
            val url = accountViewModel.startCheckout()
            url?.let { window.location.href = it }
        }
    }

    Style(AppStylesAnimations)
    Style(AppStylesGlobal)
    Style(AppStylesHeader)
    Style(AppStylesLayout)
    Style(AppStylesAccount)

    Div(attrs = { classes(AppStylesGlobal.appRoot) }) {
        // Topbar minimale
        Div(attrs = { classes(AppStylesHeader.header) }) {
            Div(attrs = { classes(AppStylesHeader.headerInner) }) {
                Div(attrs = { classes(AppStylesHeader.brandRow) }) {
                    Img("/appsydeIcon.png", attrs = {
                        classes(AppStylesHeader.brandIcon)
                        attr("alt", "AppSyde")
                    })
                    Div(attrs = { classes(AppStylesHeader.brandCol) }) {
                        Div(attrs = { classes(AppStylesHeader.brand) }) { Text("AppSyde") }
                    }
                }
                Button(attrs = {
                    classes(AppStylesHeader.logoutButton)
                    attr("type", "button")
                }) { Text("Mon compte") }
                Button(attrs = {
                    classes(AppStylesHeader.logoutButton)
                    attr("type", "button")
                    onClick { onLogout() }
                }) { Text("Déconnexion") }
            }
        }

        Div(attrs = { classes(AppStylesGlobal.global, AppStylesLayout.globalContentWrap) }) {
            Div(attrs = { classes(AppStylesAccount.billingGatePage, AppStylesAnimations.pageReveal) }) {

                // 1. Titre
                if (meInfo.isOwner) {
                    Div(attrs = { classes(AppStylesAccount.billingGateHeroTitle) }) {
                        Text("Votre essai gratuit est terminé")
                    }
                    Div(attrs = { classes(AppStylesAccount.billingGateHeroSub) }) {
                        Text("Activez votre abonnement pour rétablir l'accès au workspace.")
                    }
                } else {
                    Div(attrs = { classes(AppStylesAccount.billingGateHeroTitle) }) {
                        Text("L'essai gratuit du workspace est terminé")
                    }
                    Div(attrs = { classes(AppStylesAccount.billingGateHeroSub) }) {
                        Text("Le propriétaire du workspace doit activer l'abonnement pour rétablir l'accès à AppSyde.")
                    }
                }

                // 2. Bandeau d'état (Essai expiré / accès suspendu)
                Div(attrs = { classes(AppStylesAccount.billingGateStatusBanner) }) {
                    Div(attrs = { classes(AppStylesAccount.billingGateStatusLabel) }) { Text("Essai expiré") }
                    Div(attrs = { classes(AppStylesAccount.billingGateStatusText) }) {
                        Text("L'accès au workspace est suspendu jusqu'à l'activation d'un abonnement.")
                    }
                }

                // 3. Carte plan (même présentation que page Abonnement : prix en gros + features + CTA)
                Div(attrs = { classes(AppStylesAccount.billingGateCard) }) {
                    Div(attrs = { classes(AppStylesAccount.aboActivationPlanCardHeader) }) {
                        Div(attrs = { classes(AppStylesAccount.aboActivationPlanCardTitleBlock) }) {
                            Span(attrs = { classes(AppStylesAccount.aboActivationPlanCardTitle) }) { Text("AppSyde") }
                            Span(attrs = { classes(AppStylesAccount.aboActivationPlanCardBadge) }) { Text("Plan équipe") }
                        }
                        Div(attrs = { classes(AppStylesAccount.aboActivationPlanCardPriceBlock) }) {
                            Span(attrs = { classes(AppStylesAccount.aboActivationPlanCardPriceBig) }) { Text("99 €") }
                            Span(attrs = { classes(AppStylesAccount.aboActivationPlanCardPricePeriod) }) { Text("/ mois") }
                            Div(attrs = { classes(AppStylesAccount.aboActivationPlanCardSub) }) { Text("Jusqu'à 5 agents") }
                        }
                    }
                    Div(attrs = { classes(AppStylesAccount.aboActivationPlanFeatures) }) {
                        listOf(
                            "Boîte Gmail connectée",
                            "Copilote IA pour les réponses",
                            "Contexte automatique des tickets",
                            "Collaboration multi-agents",
                            "Historique intelligent des échanges"
                        ).forEach { feature ->
                            Div(attrs = { classes(AppStylesAccount.aboActivationPlanFeatureItem) }) {
                                Text("✓ $feature")
                            }
                        }
                    }
                    if (meInfo.isOwner) {
                        Button(attrs = {
                            classes(AppStylesAccount.aboActivationPlanCta)
                            attr("type", "button")
                            if (accountState.checkoutInProgress) attr("disabled", "true")
                            onClick { checkoutTrigger++ }
                        }) {
                            Text(if (accountState.checkoutInProgress) "Ouverture du paiement…" else "Activer AppSyde")
                        }
                        Div(attrs = { classes(AppStylesAccount.aboActivationPlanCtaSub) }) {
                            Text("Paiement sécurisé par Stripe. Annulation à tout moment.")
                        }
                    }
                    Div(attrs = { classes(AppStylesAccount.billingGateCardDivider) }) {}
                    Div(attrs = { classes(AppStylesAccount.billingGateWorkspaceLabel) }) { Text("Workspace concerné") }
                    Div(attrs = { classes(AppStylesAccount.billingGateWorkspaceLine) }) {
                        val name = meInfo.workspaceDisplayName.ifBlank { "—" }
                        val email = meInfo.account.primaryEmail
                        val agents = meInfo.membersCount
                        Text("$name • $email • $agents agent${if (agents != 1) "s" else ""}")
                    }
                }

                // 4. Réassurance (une ligne)
                Div(attrs = { classes(AppStylesAccount.billingGateReassurance) }) {
                    if (meInfo.isOwner) {
                        Text("Votre configuration, vos membres et votre historique sont conservés.")
                    } else {
                        Text("Votre accès reviendra automatiquement dès que l'abonnement sera réactivé.")
                    }
                }
            }
        }
    }
}
